import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import Globals from 'system/Globals'
import { animEventEmitter } from 'system/utils/eventEmitter'

const CHROME_HEIGHT = 34

function WEBVIEWComponent(props) {
    const refContent = useRef()
    const refWebview = useRef()
    const [posStyle, setPosStyle] = useState({})
    const [webviewUrl, setWebviewUrl] = useState(null)
    const [currentUrl, setCurrentUrl] = useState(null)
    const [iframeScale, setIframeScale] = useState(null)
    // SectionPageComponentSerializer doesn't expose Component.id, but
    // FormatSerializer's `component` field is the parent Component's PK.
    // Stash it from the first effect so the mount effect can key the
    // BrowserView pool entry on it.
    const [componentId, setComponentId] = useState(null)
    const [frameless, setFrameless] = useState(false)
    const [webviewReveal, setWebviewReveal] = useState(false)
    const [revealed, setRevealed] = useState(true)
    // Gate the BrowserView attach on the slide's animate-in lifecycle —
    // Page.runLayerAnim invokes our registered onAnim callback when the slide
    // becomes active, which works for default and controller modes alike
    // (including the first page where the standard intersection signal misses).
    const [isActive, setIsActive] = useState(false)
    const [bvFullscreen, setBvFullscreen] = useState(false)
    const [toastVisible, setToastVisible] = useState(false)
    const [currentZoom, setCurrentZoom] = useState(1)

    useEffect(() => {
        const fmt = Globals.instance().getCanvasFormat(props.model.formats, props.format)
        if (!fmt) {
            if (props.onLoad) props.onLoad(props.index)
            return
        }
        if (fmt.style) {
            setPosStyle({
                position: 'absolute',
                left: fmt.style.left,
                top: fmt.style.top,
                width: fmt.style.width,
                height: fmt.style.height,
                opacity: fmt.style.opacity
            })
        }
        const url = fmt.webview_url || null
        setWebviewUrl(url)
        setCurrentUrl(url)
        setComponentId(fmt.component != null ? fmt.component : null)
        setFrameless(!!fmt.webview_frameless)
        const isReveal = !!fmt.webview_reveal
        setWebviewReveal(isReveal)
        setRevealed(!isReveal)
        if (props.onLoad) props.onLoad(props.index)
    }, [props.model.formats, props.format]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = (e) => {
            if (!e.detail || e.detail.id !== componentId) return
            if (e.detail.event === 'webview-url-updated') setCurrentUrl(e.detail.url)
            if (e.detail.event === 'webview-bv-fullscreen') setBvFullscreen(!!e.detail.state)
            if (e.detail.event === 'webview-reveal') setRevealed(true)
        }
        window.addEventListener('window-event', handler)
        return () => window.removeEventListener('window-event', handler)
    }, [componentId])

    useEffect(() => {
        if (!bvFullscreen) { setToastVisible(false); return }
        setToastVisible(true)
        const t = setTimeout(() => setToastVisible(false), 1500)
        return () => clearTimeout(t)
    }, [bvFullscreen])

    useEffect(() => {
        if (!Globals.instance().electron) return
        if (!props.pageId) return
        const handler = (newValue) => {
            setIsActive(newValue === props.pageId)
        }
        animEventEmitter.on('valueChanged', handler)
        return () => { animEventEmitter.off('valueChanged', handler) }
    }, [props.pageId])

    useEffect(() => {
        if (!webviewUrl || !posStyle.width) return
        // Hide the BrowserView when the parent signals it should be hidden
        // (landing menu open, module overlay open, etc.). The BrowserView is
        // OS-level on top of the React renderer; effect cleanup fires
        // webview-hide and re-runs to fire webview-show when the prop flips back.
        if (Globals.instance().electron && (!isActive || props.hideWebview || !revealed)) return

        const canvasW = props.format && props.format.canvas_width
            ? parseFloat(props.format.canvas_width) : 1920
        const canvasH = props.format && props.format.canvas_height
            ? parseFloat(props.format.canvas_height) : 1080

        const leftPct  = parseFloat(posStyle.left   || '0')
        const topPct   = parseFloat(posStyle.top    || '0')
        const widthPct = parseFloat(posStyle.width  || '100')
        const heightPct= parseFloat(posStyle.height || '100')

        if (Globals.instance().electron) {
            const normalizedUrl = webviewUrl.startsWith('http://') || webviewUrl.startsWith('https://')
                ? webviewUrl : 'https://' + webviewUrl

            const id = componentId

            // Use getBoundingClientRect so bounds track the actual rendered position
            // of the WEBVIEW element — accounts for canvas letterboxing and centering
            // on non-1080p displays where the analytical % * windowH approach fails.
            const sendBounds = () => {
                if (!refWebview.current) return
                const rect = refWebview.current.getBoundingClientRect()
                const wW = window.innerWidth
                const zoom = Math.round((wW / canvasW) * 1000) / 1000
                setCurrentZoom(zoom)
                // getBoundingClientRect() returns screen pixels (post CSS scale transform).
                // CHROME_HEIGHT is a canvas CSS pixel value, so it must be scaled too.
                const scaledChromeH = frameless ? 0 : Math.round(CHROME_HEIGHT * zoom)
                window.api.send("toApp", {
                    event: "webview-show",
                    id,
                    url: normalizedUrl,
                    zoom,
                    frameless,
                    bounds: {
                        x: Math.round(rect.left),
                        y: Math.round(rect.top) + scaledChromeH,
                        width:  Math.round(rect.width),
                        height: Math.round(rect.height) - scaledChromeH
                    }
                })
            }

            sendBounds()

            // Debounce resize so intermediate frames during macOS fullscreen
            // animation don't land the BrowserView at a wrong position.
            let resizeTimer = null
            const onResize = () => {
                clearTimeout(resizeTimer)
                resizeTimer = setTimeout(sendBounds, 300)
            }
            window.addEventListener('resize', onResize)

            return () => {
                clearTimeout(resizeTimer)
                window.removeEventListener('resize', onResize)
                window.api.send("toApp", { event: "webview-hide", id })
            }
        }

        // Web iframe fallback: set iframe to authored pixel dimensions and scale down
        const wW = window.innerWidth
        const zoom = Math.round((wW / canvasW) * 1000) / 1000
        setIframeScale({
            zoom,
            w: Math.round(widthPct  / 100 * canvasW),
            h: Math.round(heightPct / 100 * canvasH)
        })
    }, [webviewUrl, posStyle, isActive, props.hideWebview, componentId, frameless, revealed]) // eslint-disable-line react-hooks/exhaustive-deps

    const sendNav = (event) => {
        if (Globals.instance().electron) window.api.send("toApp", { event, id: componentId })
    }

    const chromeBarContent = (
        <>
            <svg onClick={() => sendNav('webview-back')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            <svg onClick={() => sendNav('webview-forward')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
            </svg>
            <svg onClick={() => sendNav('webview-reload')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            <svg onClick={() => sendNav('webview-home')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <div style={{flex: 1, background: '#c8c8c8', borderRadius: '4px', height: '18px', display: 'flex', alignItems: 'center', padding: '0 6px', overflow: 'hidden'}}>
                <span style={{fontSize: '9px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4}}>
                    {currentUrl || webviewUrl || ''}
                </span>
            </div>
            <svg onClick={() => sendNav('webview-fullscreen')} width="14" height="14" viewBox="0 0 24 24" fill="#888" style={{cursor:'pointer',flexShrink:0}}>
                {bvFullscreen
                    ? <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                    : <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                }
            </svg>
        </>
    )

    const chromeBar = (
        <div style={{
            height: CHROME_HEIGHT + 'px',
            minHeight: CHROME_HEIGHT + 'px',
            background: '#c8c8c8',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            gap: '5px',
            flexShrink: 0,
            userSelect: 'none'
        }}>
            {chromeBarContent}
        </div>
    )

    if (Globals.instance().electron) {
        // BrowserViews are composited above ALL React content at the OS level,
        // so any layer with a higher DOM order would otherwise bleed into the
        // 34px chrome gap between the React chrome bar and the BrowserView start.
        // Force a high zIndex on the WEBVIEW div so its React footprint (chrome
        // + placeholder) always renders above other canvas elements, making the
        // React view consistent with what Electron actually composites.
        const electronPosStyle = { ...posStyle, zIndex: 20 }
        return (
            <>
                <div ref={refWebview} style={electronPosStyle}>
                    {webviewReveal && !revealed ? (
                        <div
                            style={{width:'100%', height:'100%', cursor:'pointer', opacity:0}}
                            onClick={() => setRevealed(true)}
                        />
                    ) : !props.hideWebview && (
                        <div style={{width:'100%', height:'100%', borderRadius:'8px', ...(frameless ? {boxShadow:'0 12px 24px rgba(0,0,0,0.25)'} : {filter:'drop-shadow(0 12px 24px rgba(0,0,0,0.25))'})}}>
                            <div style={{width:'100%', height:'100%', background: frameless ? 'transparent' : '#aaa', display:'flex', flexDirection:'column', overflow:'hidden', borderRadius:'8px'}}>
                                {!frameless && chromeBar}
                                <div style={{flex:1, background: frameless ? 'transparent' : '#fff'}} />
                            </div>
                        </div>
                    )}
                </div>
                {bvFullscreen && ReactDOM.createPortal(
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0,
                        height: Math.round(CHROME_HEIGHT * currentZoom) + 'px',
                        background: '#c8c8c8',
                        display: 'flex', alignItems: 'center',
                        padding: '0 8px',
                        gap: '5px',
                        zIndex: 9999,
                        userSelect: 'none',
                        boxSizing: 'border-box',
                    }}>
                        {chromeBarContent}
                        <div style={{
                            background: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            borderRadius: 999,
                            padding: '2px 10px',
                            fontSize: 10,
                            whiteSpace: 'nowrap',
                            opacity: toastVisible ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: 'none',
                            marginLeft: 4,
                            flexShrink: 0,
                        }}>
                            Press Esc to exit
                        </div>
                    </div>,
                    document.body
                )}
            </>
        )
    }

    const iframeStyle = iframeScale ? {
        position: 'absolute',
        top: 0,
        left: 0,
        width:  iframeScale.w + 'px',
        height: iframeScale.h + 'px',
        border: 'none',
        transform: `scale(${iframeScale.zoom})`,
        transformOrigin: 'top left'
    } : { width: '100%', height: '100%', border: 'none' }

    if (webviewReveal && !revealed) {
        return (
            <div ref={refWebview} style={{ ...posStyle, zIndex: 20, cursor: 'pointer', opacity: 0 }}
                onClick={() => setRevealed(true)}
            />
        )
    }

    return (
        <div ref={refWebview} style={{ ...posStyle, zIndex: 20 }}>
            <div style={{width:'100%', height:'100%', filter:'drop-shadow(0 12px 24px rgba(0,0,0,0.25))', borderRadius:'8px'}}>
                <div style={{width:'100%', height:'100%', background: frameless ? 'transparent' : '#aaa', display:'flex', flexDirection:'column', overflow:'hidden', borderRadius:'8px'}}>
                    {!frameless && chromeBar}
                    <div ref={refContent} style={{flex:1, background: frameless ? 'transparent' : '#fff', overflow:'hidden', position:'relative'}}>
                        {webviewUrl && (
                            <iframe
                                src={webviewUrl}
                                style={iframeStyle}
                                title="webview"
                                allow="fullscreen"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { WEBVIEWComponent as WEBVIEW }
